import React, { useState } from "react";
import { View, StyleSheet, FlatList } from "react-native";
import { Checkbox } from "react-native-paper";
import CustomModal from "../../components/common/general/CustomModal/CustomModal";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { ArrowLeft, BookOpen, DotOutline, Sparkle } from "@phosphor-icons/react";
import { CloseIcon } from "../../components/svgIcons";
import { colors } from "../../styles/design-system";

interface InteractiveLearningIntroModalProps {
  visible: boolean;
  onClose: (dontShowAgain: boolean) => void;
  moduleTitle?: string;
}

const InteractiveLearningIntroModal: React.FC<InteractiveLearningIntroModalProps> = ({ visible, onClose }) => {
  const [dontShowModalAgain, setDontShowModalAgain] = useState(false);

  const handleCancel = () => {
    onClose(dontShowModalAgain);
  };

  return (
    <CustomModal
      visible={visible}
      onRequestClose={handleCancel}
      ariaLabel="Generate Lesson Plan Modal"
      showCloseButton={false}
      bodyStyle={{ marginTop: 0 }}
      style={{ width: 512, padding: 24 }}>
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.iconContainer}>
            <Sparkle size={24} color={colors.accent.purple600} />
          </View>
          <CustomText style={styles.title} text="Welcome to Interactive Learning!" useTranslationText={false} />
          <View style={styles.closeIconWrapper}>
            <CustomButton
              styleType="transparent"
              size="xs"
              leftIcon={<CloseIcon fill="currentColor" />}
              onPress={handleCancel}
              aria-label="Close modal"
            />
          </View>
        </View>

        <View style={styles.content}>
          <CustomText
            containsHtml
            text="You're about to start an Interactive Learning Experience (ILE) - a guided, hands-on way to deeply understand this topic through exploration and practice."
            style={styles.description}
          />

          <View style={[styles.sectionsContainer, styles.whatToExpect]}>
            <View style={styles.sectionRow}>
              <BookOpen size={16} color={colors.accent.purple600} />
              <CustomText text="What to expect" style={styles.sectionTitle} />
            </View>
            <FlatList
              data={[
                {
                  id: "interactive-content",
                  title: "Interactive content with real-world examples",
                },
                { id: "learning-objectives", title: "Learning objectives tracked on the left panel" },
                { id: "additional-resources", title: "Additional resources in the Explore panel" },
                { id: "practical-scenarios", title: "Practice scenarios to test your understanding" },
              ]}
              renderItem={({ item }) => (
                <View style={styles.objectives}>
                  <DotOutline weight="fill" size={20} />
                  <CustomText text={item.title} style={styles.objectivesText} />
                </View>
              )}
              keyExtractor={(item) => item.id}
              contentContainerStyle={styles.objectivesWrapper}
            />
          </View>
          <View style={[styles.sectionsContainer, styles.needToExit]}>
            <View style={styles.sectionRow}>
              <ArrowLeft size={16} color={colors.brand[600]} />
              <CustomText text="Need to exit?" style={styles.sectionTitle} />
            </View>
            <CustomText
              containsHtml
              text={`Click "Back to Chat" in the top-left corner anytime. Your progress is automatically saved.`}
              style={styles.sectionDescription}
            />
          </View>
          <View style={[styles.sectionsContainer, styles.resumeLater]}>
            <View style={styles.sectionRow}>
              <Sparkle size={16} color={colors.success[600]} />
              <CustomText text="Want to resume later?" style={styles.sectionTitle} />
            </View>
            <CustomText
              containsHtml
              text="Find this activity in the Learning Activities page (accessible from the sidebar). You can pick up right where you left off!"
              style={styles.sectionDescription}
            />
          </View>
          <View style={styles.buttonContainer}>
            <View style={styles.checkboxContainer}>
              <Checkbox
                status={dontShowModalAgain ? "checked" : "unchecked"}
                onPress={() => setDontShowModalAgain((prev) => !prev)}
                theme={{ colors: { primary: colors.brand[500] } }}
              />
              <CustomText text="Don't show this again" size="s" style={styles.checkboxLabel} />
            </View>
            <CustomButton
              text="Get Started"
              onPress={handleCancel}
              size="m"
              style={styles.getStartedButton}
              textStyle={styles.getStartedButtonText}
            />
          </View>
        </View>
      </View>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
    width: "100%",
    flex: 1,
    height: "100%",
  },
  header: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
    position: "relative",
    gap: 12,
  },
  iconContainer: {
    width: 48,
    height: 48,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.accent.purple100,
    borderRadius: "50%",
  },
  closeIconWrapper: {
    position: "absolute",
    top: 0,
    right: 0,
  },
  title: {
    fontSize: 24,
    fontWeight: "600",
    letterSpacing: -0.6,
  },
  content: {
    paddingTop: 22,
    gap: 16,
    flex: 1,
  },
  description: {
    color: "#667085",
    lineHeight: 26,
    fontSize: 16,
    fontWeight: "500",
  },
  sectionsContainer: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    borderStyle: "solid",
  },
  sectionRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    gap: 8,
  },
  checkboxButton: {
    padding: 0,
    marginRight: 12,
    marginTop: 2,
  },
  sectionContent: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: colors.grey[900],
  },
  sectionDescription: {
    fontSize: 14,
    lineHeight: 21,
    color: colors.grey[700],
  },
  whatToExpect: {
    marginTop: 16,
    borderColor: colors.accent.purple100,
    backgroundColor: colors.accent.purple50,
  },
  needToExit: {
    borderColor: colors.brand[100],
    backgroundColor: colors.brand[50],
  },
  resumeLater: {
    borderColor: colors.success[100],
    backgroundColor: colors.success[50],
  },
  objectivesWrapper: {
    gap: 8,
    paddingLeft: 4,
  },
  objectives: {
    flexDirection: "row",
    alignItems: "center",
  },
  objectivesText: {
    fontSize: 14,
    lineHeight: 21,
    color: colors.grey[700],
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
    paddingTop: 16,
  },
  getStartedButton: {
    backgroundColor: "#3E68FE",
    borderWidth: 2,
    borderColor: "#3E68FE",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  getStartedButtonText: {
    color: colors.white,
    fontSize: 14,
  },
  checkboxContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  checkboxLabel: {
    color: colors.grey[700],
  },
});

export default InteractiveLearningIntroModal;
